import { showToast } from "@certn/cascadia/components/toast/utils";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { APIError, defaultParseError } from "./errors";
import { fetcher } from "./fetcher";

export interface UseSubmitOptions<Response> {
  throwErrors?: boolean;
  showToastOnError?: string | boolean;
  parseError?: (error: APIError) => string | undefined;
  onSuccess?: (data: Response) => unknown;
  onFailure?: (error: unknown) => void;
}

export function useSubmit<Response extends unknown, RequestBody = unknown>(
  path: string,
  options: UseSubmitOptions<Response> = {}
) {
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<unknown>();
  const [response, setResponse] = useState<Response>();

  const submit = useCallback(
    async (body: RequestBody = {} as RequestBody, init: RequestInit = {}) => {
      setResponse(undefined);
      setError(undefined);

      try {
        setIsSubmitting(true);
        const res = await fetcher.submit<Response>(path, body, init);
        setResponse(res);

        if (options.onSuccess) {
          try {
            await options.onSuccess(res);
          } catch (onSuccessError) {
            if (options.onFailure) {
              options.onFailure(onSuccessError);
            } else {
              // Re-throw to maintain visibility of errors when no onFailure handler is provided
              throw onSuccessError;
            }
          }
        }

        return res;
      } catch (e: unknown) {
        setError(e);

        if (options.onFailure) {
          options.onFailure(e);
        }

        const defaultMessage = t("Something went wrong");
        let errorToast = defaultMessage;
        if (e instanceof APIError) {
          errorToast =
            options.parseError?.(e) ??
            defaultParseError(e) ??
            e.detail ??
            defaultMessage;
        }

        if (options.showToastOnError) {
          showToast(
            typeof options.showToastOnError === "string"
              ? options.showToastOnError
              : errorToast,
            { type: "error" }
          );
        }

        if (options.throwErrors) {
          throw e;
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      options.throwErrors,
      options.onSuccess,
      options.onFailure,
      options.parseError,
      path,
    ]
  );

  return {
    submit,
    response,
    isSubmitting,
    error,
  };
}
